import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";
import { Button } from "./ui/button";
import { Sun, Moon, SignOut, MapPin, SquaresFour, ChartBar, Gear, Calendar, Tag } from "@phosphor-icons/react";
import BugReportButton from "./BugReportButton";
import NavbarTruckSearch from "./NavbarTruckSearch";
import api from "../lib/api";
import { trackSiteEvent } from "../lib/analytics";

function Logo() {
  return (
    <svg
      viewBox="0 0 32 32"
      className="h-8 w-8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect width="32" height="32" rx="9" fill="hsl(var(--primary))" />
      <path
        d="M7 12h11v8H7zM18 14.5h4l3 3v2.5h-7z"
        stroke="white"
        strokeWidth="1.6"
        strokeLinejoin="round"
        fill="none"
      />
      <circle cx="11.5" cy="21.5" r="1.8" fill="white" />
      <circle cx="21.5" cy="21.5" r="1.8" fill="white" />
    </svg>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // New-inquiry count for the Dashboard nav-icon dot. Polled every 60s
  // for owner-role users so they notice a fresh inquiry without needing
  // to check email. Everyone else gets a no-op.
  const [newInquiryCount, setNewInquiryCount] = useState(0);
  useEffect(() => {
    if (user?.role !== "owner") return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get("/owner/event-inquiries", { params: { limit: 1 } });
        if (!cancelled) setNewInquiryCount(r.data?.new_count || 0);
      } catch {
        // Silently ignore — a nav dot is never worth a toast
      }
    };
    tick();
    const timer = setInterval(tick, 60_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [user?.role, user?.id]);

  // Phase 1F — smart Calendar routing. Pure-staff users (signed up
  // without a truck but invited as crew somewhere) were landing on
  // the owner planning page (`/calendar`) which prompts them to
  // "Connect calendar / Add a stop on your Dashboard" — confusing
  // because they don't run a truck. We look up their linked staff
  // invites via /me/staff-invites; if they have ANY active invite
  // AND are NOT an owner, route Calendar → their /staff/:token
  // schedule. Owners always go to /calendar (their planning surface).
  // Logged-out users keep the existing /calendar route (which shows
  // the empty state).
  // A staff token remembered on-device by StaffView.jsx when the crew
  // member opened their invite link. This makes the Calendar tab work
  // for crew with NO TruckTrackr account (or logged in under a
  // different email than the invite) — the previous account-linked
  // lookup alone left them stranded on the owner planning page.
  const storedStaffToken = (() => {
    try { return localStorage.getItem("tt_staff_token") || ""; } catch { return ""; }
  })();
  const [calendarHref, setCalendarHref] = useState(
    storedStaffToken ? `/staff/${storedStaffToken}` : "/calendar"
  );
  useEffect(() => {
    if (user?.role === "owner") {
      // Owners always get their own planning surface, even if they
      // once opened someone else's staff link on this device.
      setCalendarHref("/calendar");
      return;
    }
    // Logged-out or customer-role with a remembered staff link →
    // route straight back to their crew schedule.
    if (storedStaffToken) {
      setCalendarHref(`/staff/${storedStaffToken}`);
      return;
    }
    if (!user) {
      setCalendarHref("/calendar");
      return;
    }
    // Logged-in non-owner without a device-remembered link — fall back
    // to the account-linked lookup (invite email matched their account).
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/me/staff-invites");
        if (cancelled) return;
        const invites = (r.data || []).filter((i) => i.status !== "revoked");
        if (invites.length > 0 && invites[0].token) {
          setCalendarHref(`/staff/${invites[0].token}`);
        } else {
          setCalendarHref("/calendar");
        }
      } catch {
        if (!cancelled) setCalendarHref("/calendar");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.role, storedStaffToken, location.pathname]);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-xl bg-background/70 border-b border-border pt-[var(--safe-area-inset-top,env(safe-area-inset-top))]"
      data-testid="main-navbar"
    >
      <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 h-16 flex items-center gap-2 sm:gap-4">
        <Link to="/" className="flex items-center gap-2 flex-none" data-testid="nav-home-link">
          <Logo />
          <span className="font-display font-bold text-lg tracking-tight hidden sm:inline">TruckTrackr</span>
        </Link>

        {/* Global "Find your favorite truck" search — visible on every page,
            navbar-prominent, full-roster fuzzy match with autocomplete. */}
        <div className="flex-1 min-w-0 max-w-md overflow-hidden">
          <NavbarTruckSearch />
        </div>

        <nav className="flex items-center gap-0.5 sm:gap-2 flex-none">
          <Link to="/map" data-testid="nav-map-link">
            <Button
              variant={location.pathname === "/map" ? "secondary" : "ghost"}
              size="sm"
              className="gap-1 px-2 sm:px-3"
            >
              <MapPin weight="duotone" className="h-4 w-4" />
              <span className="hidden sm:inline">Map</span>
            </Button>
          </Link>

          {/* Calendar — the owner ops calendar (Phase 1A / 1B). Visible
              to everyone (anonymous + signed-in); shows an empty state
              for anonymous visitors. The Google/Outlook sync wizard
              lives under a "Connect calendar" button INSIDE this page
              now (it used to be the destination of this nav link).
              Icon-only on mobile (matches the "Map" pattern), label
              appears on `sm+` so phone users still get the entry-point. */}
          <Link to={calendarHref} data-testid="nav-calendar-link">
            <Button
              variant={location.pathname === "/calendar" || location.pathname.startsWith("/staff/") ? "secondary" : "ghost"}
              size="sm"
              className="gap-1 px-2 sm:px-3"
            >
              <Calendar weight="duotone" className="h-4 w-4" />
              <span className="hidden sm:inline">Calendar</span>
            </Button>
          </Link>

          {/* Pricing stays for anonymous visitors on all sizes; for signed-in
              users it's desktop-only — on narrow phones the owner navbar has
              8 buttons and the search box was overlapping the icons. */}
          <Link to="/pricing" data-testid="nav-pricing-link" onClick={() => trackSiteEvent("nav_pricing_click")} className={user ? "hidden sm:block" : ""}>
            <Button
              variant={location.pathname === "/pricing" ? "secondary" : "ghost"}
              size="sm"
              className="gap-1 px-2 sm:px-3"
            >
              <Tag weight="duotone" className="h-4 w-4" />
              <span className="hidden sm:inline">Pricing</span>
            </Button>
          </Link>

          {user?.role === "owner" && (
            <Link to="/dashboard" data-testid="nav-dashboard-link">
              <Button
                variant={location.pathname === "/dashboard" ? "secondary" : "ghost"}
                size="sm"
                className="gap-1 relative"
              >
                <SquaresFour weight="duotone" className="h-4 w-4" />
                <span className="hidden sm:inline">Dashboard</span>
                {newInquiryCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 h-4 min-w-4 px-1 grid place-items-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold leading-none shadow-sm ring-2 ring-background"
                    aria-label={`${newInquiryCount} new inquir${newInquiryCount === 1 ? "y" : "ies"}`}
                    data-testid="nav-inquiry-dot"
                  >
                    {newInquiryCount > 9 ? "9+" : newInquiryCount}
                  </span>
                )}
              </Button>
            </Link>
          )}

          {user?.is_admin && (
            <Link to="/admin" data-testid="nav-admin-link">
              <Button
                variant={location.pathname === "/admin" ? "secondary" : "ghost"}
                size="sm"
                className="gap-1"
              >
                <ChartBar weight="duotone" className="h-4 w-4" />
                <span className="hidden sm:inline">Admin</span>
              </Button>
            </Link>
          )}

          <BugReportButton testId="nav-bug-report-btn" />

          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            data-testid="theme-toggle-btn"
            aria-label="Toggle theme"
            className="h-9 w-9 sm:h-10 sm:w-10"
          >
            {theme === "dark" ? (
              <Sun weight="duotone" className="h-5 w-5" />
            ) : (
              <Moon weight="duotone" className="h-5 w-5" />
            )}
          </Button>

          {user ? (
            <>
              <Link to="/settings" data-testid="nav-settings-link" className="inline-flex">
                <Button variant="ghost" size="sm" className="gap-1 px-2 sm:px-3" aria-label="Account settings">
                  <Gear weight="duotone" className="h-4 w-4" />
                  <span className="hidden sm:inline">Settings</span>
                </Button>
              </Link>
              <Button
                variant="ghost"
                size="sm"
                onClick={onLogout}
                data-testid="nav-logout-btn"
                className="gap-1 px-2 sm:px-3"
              >
                <SignOut weight="duotone" className="h-4 w-4" />
                <span className="hidden sm:inline">Sign out</span>
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-link">
                <Button variant="ghost" size="sm" className="px-2 sm:px-3">
                  Log in
                </Button>
              </Link>
              <Link to="/signup" data-testid="nav-signup-link">
                <Button size="sm" className="px-2.5 sm:px-3 bg-primary text-primary-foreground hover:bg-primary/90">
                  Sign up
                </Button>
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
